/**
 * JanetPageContext — lets any module page register its current entity
 * so the Ask Janet button (TopBar + FAB) opens pre-loaded with context, and
 * owns WHICH Janet topic (janet_chat_sessions.id) is active in this window.
 *
 * Usage in a detail page:
 *   const { setJanetPage } = useJanetPage()
 *   useEffect(() => {
 *     setJanetPage({
 *       entityType: 'deal',
 *       entityName: deal.title,
 *       entityContext: `Deal type: ${deal.deal_type}. Counterparty: ${deal.studio_name}.`,
 *       suggestedContacts: [{ id, name, email, role: 'Counterpart' }, ...],
 *     })
 *     return () => setJanetPage(null)
 *   }, [deal?.id])
 *
 * Topic state is persisted in localStorage (janet.chat.activeSession) so a
 * refresh resumes the same conversation, and the desktop Janet window / a
 * browser popup can be handed a topic via ?topic=<id>.
 */
import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { newSessionId } from '../lib/janetTopics'
import { IS_DEMO } from '../lib/demoClient'

const ACTIVE_KEY = 'janet.chat.activeSession'

function readActive() {
  // The trailer shares one account across every visitor: a topic must never
  // survive a visit (DEMO-ENVIRONMENT §0, "Janet's history is shared").
  if (IS_DEMO) return null
  try {
    const q = new URLSearchParams(window.location.search).get('topic')
    if (q) { localStorage.setItem(ACTIVE_KEY, q); return q }
  } catch { /* no URL / storage access */ }
  try { return localStorage.getItem(ACTIVE_KEY) || null } catch { return null }
}

const JanetPageContext = createContext({
  page: null,           // { entityType, entityName, entityContext, suggestedContacts }
  isOpen: false,
  setJanetPage: () => {},
  openJanet: () => {},
  closeJanet: () => {},
  activeSessionId: null,
  pickTopic: () => {},
  newTopic: () => null,
  pttPending: false,
  clearPttPending: () => {},
  compactDockExpanded: false,
  setCompactDockExpanded: () => {},
})

export function JanetPageProvider({ children }) {
  const [page, setPage]     = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [activeSessionId, setActiveSessionId] = useState(() => readActive() || newSessionId())
  const [pttPending, setPttPending] = useState(false)
  // Whether Home's compact (below-`lg`) Janet dock is currently expanded to
  // the full panel — set by HomeJanetDockMobile, read by Layout.jsx so a
  // fixed-position element sharing that screen corner (BugReportFAB) can
  // stay clear of the panel's own controls (composer, Send) rather than
  // guessing a fixed offset that only clears the collapsed bar (2026-09-30
  // repair, issue #178). Not itself a layout decision — just the one piece
  // of state a sibling of the dock has no other way to observe.
  const [compactDockExpanded, setCompactDockExpanded] = useState(false)

  const setJanetPage = useCallback((ctx) => setPage(ctx || null), [])
  const openJanet    = useCallback(() => setIsOpen(true),  [])
  const closeJanet   = useCallback(() => { setIsOpen(false); setPttPending(false) }, [])

  const pickTopic = useCallback((id) => {
    if (!id) return
    setActiveSessionId(id)
    if (IS_DEMO) return
    try { localStorage.setItem(ACTIVE_KEY, id) } catch { /* storage unavailable — in-memory only */ }
  }, [])
  const newTopic = useCallback(() => {
    const id = newSessionId()
    pickTopic(id)
    return id
  }, [pickTopic])
  const clearPttPending = useCallback(() => setPttPending(false), [])

  // Desktop shell global hotkey dispatches this event to summon the Janet FAB.
  useEffect(() => {
    window.addEventListener('janet:summon', openJanet)
    return () => window.removeEventListener('janet:summon', openJanet)
  }, [openJanet])

  // App-wide right-Option hold opens the FAB and starts listening (the panel
  // itself owns the hotkey while mounted; this covers the CLOSED state).
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code !== 'AltRight' || e.repeat) return
      if (isOpen) return
      setPttPending(true)
      setIsOpen(true)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen])

  return (
    <JanetPageContext.Provider value={{ page, isOpen, setJanetPage, openJanet, closeJanet, activeSessionId, pickTopic, newTopic, pttPending, clearPttPending, compactDockExpanded, setCompactDockExpanded }}>
      {children}
    </JanetPageContext.Provider>
  )
}

export function useJanetPage() {
  return useContext(JanetPageContext)
}
